import React, { useEffect, useRef } from "react";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import "../../styles/DatePicker.css";
import { Label, FormGroup } from "reactstrap";
import { DATE_FORMAT } from "../../utils/Constants.tsx";

interface DateInputProps {
  label: string;
  value: string;
  maxDate?: Date;
  minDate?: Date;
  placeholder?: string;
  onChange: (dateStr: string) => void;
  isRequired?: boolean;
  disabled?: boolean;
}

const DatePicker: React.FC<DateInputProps> = ({
  label,
  value,
  maxDate,
  minDate,
  placeholder,
  onChange,
  isRequired,
  disabled
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<any>(null); // Reference to the Flatpickr instance

  useEffect(() => {
    if (inputRef.current) {
      calendarRef.current = flatpickr(inputRef.current, {
        dateFormat: DATE_FORMAT.d_m_Y,
        allowInput: true,
        defaultDate: value || undefined,
        maxDate: maxDate ? maxDate : null,
        minDate: minDate ? minDate : null,
        disableMobile: true,
        onChange: (_, dateStr) => {
          onChange(dateStr);
        },
        onReady: (selectedDates, dateStr, instance) => {
          // Add the clear button once the calendar is ready
          const clearButton = document.createElement("button");
          clearButton.innerText = "Clear";
          clearButton.type = "button";
          clearButton.className = "flatpickr-clear-button";
          clearButton.addEventListener("click", () => {
            instance.clear();
            onChange("");
          });

          const calendarContainer = instance.calendarContainer;
          if (
            calendarContainer &&
            !calendarContainer.querySelector(".flatpickr-clear-button")
          ) {
            calendarContainer.appendChild(clearButton);
          }
        },
      });

      return () => {
        calendarRef.current?.destroy();
      };
    }
  }, [value, onChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const digitsOnly = rawValue.replace(/\D/g, "");
    const formattedValue =
      digitsOnly.length <= 2
        ? digitsOnly
        : digitsOnly.length <= 4
        ? `${digitsOnly.slice(0, 2)}-${digitsOnly.slice(2)}`
        : `${digitsOnly.slice(0, 2)}-${digitsOnly.slice(
            2,
            4
          )}-${digitsOnly.slice(4, 8)}`;
    onChange(formattedValue);
  };

  const handleCalendarOpen = () => {
    calendarRef.current?.open(); // Open the calendar programmatically
  };

  return (
    <FormGroup className={`formgroup  ${disabled ? 'disabled-opacity': ''}`}>
      {label && (
        <Label className="custom-label">
          {label}
          {isRequired && <span className="required-asterisk">*</span>}
        </Label>
      )}

      <div className="date-picker-container date-picker-parent">
        <input
          type="text"
          ref={inputRef}
          className="custom-date-picker"
          placeholder={placeholder || "DD-MM-YYYY"}
          value={value}
          onChange={handleInputChange}
          disabled={disabled}
        />
        <img
          src={require("../../assets/images/calendar.svg").default}
          alt="Calendar Icon"
          onClick={handleCalendarOpen}
          className="date-picker-img"
        />
      </div>
    </FormGroup>
  );
};

export default DatePicker;
